import { sendEmailChallenge } from '@client/new/api/auth.api';
import { makeAsync } from '@client/new/helpers/async';
import { navigate, PATHS } from '@client/routes';
import { Async } from '@client/new/helpers/types';
import { useCallback, useState } from 'react';
import { useLoginStore } from '../../../../stores/login.store';

export type LoginFormValues = {
  email: string;
};

export const ACCOUNT_LOOKUP_MESSAGE =
  "If the email exists, we'll send a confirmation code to that address.";
const GENERIC_LOGIN_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const normalizeLoginEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

type AxiosErrorInfo =
  | { type: 'not-axios-error' }
  | { type: 'axios-error'; status?: number; message: string };

const isAxiosLikeError = (
  err: unknown,
): err is { isAxiosError: true; response?: unknown } => {
  if (!err || typeof err !== 'object') {
    return false;
  }

  return 'isAxiosError' in err && err.isAxiosError === true;
};

const extractAxiosErrorStatus = (response: unknown): number | undefined => {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  if ('status' in response && typeof response.status === 'number') {
    return response.status;
  }

  if (
    'data' in response &&
    response.data &&
    typeof response.data === 'object'
  ) {
    const responseData = response.data as { statusCode?: unknown };
    if (typeof responseData.statusCode === 'number') {
      return responseData.statusCode;
    }
  }

  return undefined;
};

const extractAxiosErrorMessage = (responseData: unknown): string => {
  if (typeof responseData === 'string') {
    return responseData;
  }

  if (
    responseData &&
    typeof responseData === 'object' &&
    'message' in responseData &&
    typeof responseData.message === 'string'
  ) {
    return responseData.message;
  }

  return '';
};

const toAxiosErrorInfo = (err: unknown): AxiosErrorInfo => {
  if (!isAxiosLikeError(err)) {
    return { type: 'not-axios-error' };
  }

  return {
    type: 'axios-error',
    status: extractAxiosErrorStatus(err.response),
    message: extractAxiosErrorMessage(
      typeof err.response === 'object' && err.response !== null
        ? (err.response as { data?: unknown }).data
        : undefined,
    ),
  };
};

const mapLoginErrorMessage = (err: unknown): string => {
  const axiosError = toAxiosErrorInfo(err);

  if (axiosError.type !== 'axios-error') {
    return GENERIC_LOGIN_ERROR_MESSAGE;
  }

  const normalizedMessage = axiosError.message.trim().toLowerCase();
  const hasNotFoundStatus = axiosError.status === 404;
  const hasAccountNotFoundMessage = normalizedMessage.includes(
    'account does not exist',
  );
  const hasRegistrationRequiredMessage =
    normalizedMessage === 'registration_required';

  if (
    hasNotFoundStatus ||
    hasAccountNotFoundMessage ||
    hasRegistrationRequiredMessage
  ) {
    return ACCOUNT_LOOKUP_MESSAGE;
  }

  return GENERIC_LOGIN_ERROR_MESSAGE;
};

type UseLoginPageSubmitResult = {
  submitState: Async<null>;
  submitLogin: (values: LoginFormValues) => Promise<void>;
};

export const useLoginPageSubmit = (): UseLoginPageSubmitResult => {
  const { setEmail } = useLoginStore();
  const [submitState, setSubmitState] = useState<Async<null>>(
    makeAsync.notFetched(),
  );

  const submitLogin = useCallback(
    async (values: LoginFormValues) => {
      setSubmitState(makeAsync.fetching());
      const email = normalizeLoginEmail(values.email);

      try {
        await sendEmailChallenge(email, 'login');
        setEmail(email);
        navigate(PATHS.LOGIN_CONFIRMATION_CODE);
      } catch (err: unknown) {
        setSubmitState(makeAsync.error(mapLoginErrorMessage(err)));
      }
    },
    [setEmail],
  );

  return {
    submitState,
    submitLogin,
  };
};
